/**
 * Export for one Leads table (#885), on the rules #869 set for Biler: hidden
 * without leads_table_exporter (or admin) and when the table is empty. A paged
 * table passes `loadData`, which fetches up to LEADS_EXPORT_LIMIT rows from the
 * applied query on click; `capped` adds the note that the export stops there.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../components/ExportButtons'
import type { ColumnDefinition } from '../../utils/exportToCsv'
import { localizeApiDetail } from '../../i18n/apiError'
import { LEADS_EXPORT_LIMIT, useKanEksportere } from './eksportTilgang'

interface Props {
  rows: unknown[]
  columns: ColumnDefinition[]
  title: string
  filters?: string[]
  loadData?: () => Promise<unknown[]>
  capped?: boolean
  testId: string
}

export default function LeadsTableExport({ rows, columns, title, filters, loadData, capped, testId }: Props) {
  const { t } = useTranslation('leads')
  const kanEksportere = useKanEksportere()
  const [error, setError] = useState<string | null>(null)

  if (!kanEksportere || rows.length === 0) return null

  const load = loadData && (() => {
    setError(null)
    return loadData().catch(err => {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message)
      throw err
    })
  })

  return (
    <div className="flex items-center justify-end gap-3 flex-wrap mb-3" data-testid={testId}>
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
      {capped && (
        <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={`${testId}-cap`}>
          {t('tableExport.capNote', { n: LEADS_EXPORT_LIMIT })}
        </span>
      )}
      <ExportButtons
        data={rows}
        loadData={load}
        columns={columns}
        metadata={{ title, filters: filters?.length ? filters : undefined }}
      />
    </div>
  )
}
